function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useMemo } from 'react';
import { Decimal } from 'decimal.js';
import compact from 'lodash/compact';
import { Controller, useFormContext } from 'react-hook-form';
import { TableList, TextField, Typography, WithStatusIcon } from 'seagull';
import { getUnitName, pluralizeUnitName } from "../../../../utils/unit-name.utils";
import { connectControllerToTextField } from "../../../../utils/react-hook-form.utils";
import styles from "./BillServiceModal.module.scss";
import { serviceModel } from "../../../../models";
import { utils } from "../../../../utils";
import { getQuantityWarning, getTableDefs, getTotal, qtyWarningToText } from "./BillServiceModalTable.utils";
const maxQuantity = new Decimal(999999);
const maxPrice = new Decimal(999999);
const maxError = 'max';
const requiredError = 'required';
export default function BillServiceModalTable(props) {
  const {
    service,
    billingContext,
    disableAll
  } = props;
  const {
    issueDate,
    manualServiceBilling
  } = billingContext || {};
  const {
    control,
    watch
  } = useFormContext();
  const cost = service.cost;
  const isVariablePrice = serviceModel.isVariablePrice(cost);
  const isRangePrice = serviceModel.isRangePrice(cost);
  const priceInput = watch('price');
  const quantityInput = watch('quantity');
  const quantity = useMemo(() => quantityInput ? new Decimal(+quantityInput) : new Decimal(0), [quantityInput]);
  const total = useMemo(() => getTotal(service, quantity, priceInput), [quantity, priceInput, service]);
  const {
    columnDefs,
    headerItems
  } = useMemo(() => getTableDefs(service), [service]);
  const quantityWarning = getQuantityWarning(service, quantity, issueDate, manualServiceBilling);
  return /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs
  }, /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "bill-service-table-header"
  }), /*#__PURE__*/React.createElement(TableList.Row, {
    disableShadow: true,
    items: compact([{
      value: /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true
      }, service.name)
    }, isVariablePrice && {
      value: /*#__PURE__*/React.createElement(Controller, {
        control: control,
        name: "quantity",
        rules: {
          validate: quantity => {
            if (!quantity) return requiredError;
            const asNumber = new Decimal(+quantity);
            if (asNumber.lessThanOrEqualTo(0)) return requiredError;
            if (asNumber.greaterThan(maxQuantity)) return maxError;
          }
        },
        render: renderProps => {
          const {
            fieldState: {
              error
            }
          } = renderProps;
          const isWarning = !!quantityWarning && !error;
          return /*#__PURE__*/React.createElement(TextField, _extends({
            isMoney: true,
            disabled: disableAll,
            size: "small",
            width: 90,
            inputProps: {
              min: 0
            }
          }, connectControllerToTextField(renderProps, {
            noHelperText: true,
            alwaysShowErrors: true
          }), {
            testid: "bill-service-table-quantity",
            warning: isWarning,
            suffix: (error || !!quantityWarning) && /*#__PURE__*/React.createElement(WithStatusIcon, {
              "data-testid": "bill-service-table-quantity-warning-message",
              "data-intent": error ? maxError : quantityWarning || '',
              info: (error === null || error === void 0 ? void 0 : error.message) === maxError ? "Can't input more than ".concat(utils.formatNumberWithCommas(maxQuantity), " ").concat(pluralizeUnitName(maxQuantity, cost.unitName)) : quantityWarning ? qtyWarningToText[quantityWarning] : 'Please enter a positive number',
              statusType: isWarning ? 'warning' : 'error',
              size: "medium"
            })
          }));
        }
      })
    }, isVariablePrice ? {
      value: cost.unitCap ? /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "bill-service-table-preapproved-units"
      }, utils.formatNumberWithCommas(cost.unitCap)) : null
    } : isRangePrice ? {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "bill-service-table-rate-range"
      }, utils.getPriceRangeText(cost))
    } : null, {
      value: isRangePrice ? /*#__PURE__*/React.createElement(Controller, {
        control: control,
        name: "price",
        rules: {
          validate: value => {
            if (!value) return requiredError;
            const asNumber = new Decimal(+value);
            if (asNumber.greaterThan(maxPrice)) return maxError;
          }
        },
        render: renderProps => {
          const {
            field,
            fieldState: {
              error
            }
          } = renderProps;
          const isWarning = field.value !== '' && cost.maxPrice.lessThan(field.value);
          return /*#__PURE__*/React.createElement(TextField, _extends({
            isMoney: true,
            disabled: disableAll
          }, connectControllerToTextField(renderProps, {
            noHelperText: true,
            alwaysShowErrors: true
          }), {
            size: "small",
            inputProps: {
              min: 0
            },
            testid: "bill-service-table-rate",
            warning: isWarning && !error,
            suffix: (error || isWarning) && /*#__PURE__*/React.createElement(WithStatusIcon, {
              "data-testid": "bill-service-table-rate-warning-message",
              info: (error === null || error === void 0 ? void 0 : error.message) === maxError ? "Can't input more than ".concat(utils.formatDollarAmount(maxPrice)) : isWarning ? "Payment above the maximum of ".concat(utils.formatDollarAmount(cost.maxPrice), " will require your client's approval") : 'Please enter a positive number',
              statusType: error ? 'error' : 'warning',
              size: "medium"
            })
          }));
        }
      }) : /*#__PURE__*/React.createElement("div", {
        className: styles.rateWithUnit
      }, /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "bill-service-table-rate-display"
      }, utils.formatDollarAmount(cost.price)), isVariablePrice && /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        variant: "paragraph2",
        skin: "secondary"
      }, "/", getUnitName(cost.unitName)))
    }, {
      value: cost.discount ? /*#__PURE__*/React.createElement(Typography, null, utils.formatDiscount(cost.discount)) : null
    }, {
      value: /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "bill-service-table-total"
      }, utils.formatDollarAmount(total))
    }])
  }));
}